import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import {
  type IndexdbStorage,
  provideIndexdbStorage
} from '@bcf-vanilla-ts-v1-utilities/indexdb-storage/indexdb-storage';
import { addDays, isAfter } from 'date-fns';
import type { UseStore } from 'idb-keyval';

type PwaMemoryKeys = 'pwa-install-bar-dimissed-date' | 'pwa-installed-ios';
const pwaMemoryKey: string = 'pwa-memory';

export class PwaMemory {
  private _storeRef: UseStore | undefined;

  constructor(private _indexdbStorage: IndexdbStorage) {}

  private async _getStore(): Promise<UseStore> {
    if (this._storeRef) {
      return this._storeRef;
    }
    const store: UseStore = await this._indexdbStorage.createStore(pwaMemoryKey);
    this._storeRef = store;
    return store;
  }

  public async shouldShowBar(): Promise<boolean> {
    const key: PwaMemoryKeys = 'pwa-install-bar-dimissed-date';
    const useStore: UseStore = await this._getStore();

    const setDate: Date | undefined = await this._indexdbStorage.get(key, useStore);
    return this._checkBarIn7DaysWindow(setDate);
  }

  public async shouldShowBarForIOS(): Promise<boolean> {
    const key: PwaMemoryKeys = 'pwa-installed-ios';
    const useStore: UseStore = await this._getStore();
    const isInstalled: boolean = (await this._indexdbStorage.get(key, useStore)) || false;
    return !isInstalled;
  }

  public async setAsShownBar(): Promise<void> {
    const key: PwaMemoryKeys = 'pwa-install-bar-dimissed-date';
    const useStore: UseStore = await this._getStore();
    await this._indexdbStorage.set(key, new Date(), useStore);
  }

  public async setAsInstalledForIOS(): Promise<void> {
    const key: PwaMemoryKeys = 'pwa-installed-ios';
    const useStore: UseStore = await this._getStore();
    await this._indexdbStorage.set(key, true, useStore);
  }

  private _checkBarIn7DaysWindow(setDate: Date | undefined): boolean {
    if (!setDate) {
      return true;
    }
    if (isAfter(new Date(), addDays(setDate, 7))) {
      return true;
    }
    return false;
  }
}

export function providePwaMemory(): PwaMemory {
  return provideSingleton(PwaMemory, () => new PwaMemory(provideIndexdbStorage()));
}
